/**
 * Timbrica — AI Upscale Core (Real-ESRGAN x4, browser). enhance-video Phase 3.
 *
 * ARCHITECTURE (2026-05-17, feedback #433): this file is a thin MAIN-THREAD
 * PROXY. All ONNX compile + inference runs in `ai-upscale-worker.js` — the
 * etalon "dedicated worker" pattern (Piper / Whisper / Silero / DeepFilter).
 * Previously the ESRGAN session ran on the main thread; backgrounding the tab
 * mid-inference froze it and the export silently died. A worker keeps the UI
 * responsive and survives background-tab throttling.
 *
 * The model is fetched + SHA-verified HERE (main thread — async I/O, doesn't
 * block, and keeps that path unit-testable), then the verified bytes are
 * transferred to the worker, which compiles the session and runs inference.
 *
 * Public surface (window.AIUpscaleCore) — unchanged from the pre-worker API:
 *   AIUpscaleCore.checkCapabilities() → { supported, provider, reason, ... }
 *   AIUpscaleCore.probe()             → Promise<bool>  (live WebGPU probe)
 *   const core = new AIUpscaleCore({ modelUrls, tileSize, overlap })
 *   await core.init({ onProgress })
 *   const outCanvas = await core.upscaleFrame(inputCanvas, { onTileProgress })
 *   core.requestCancel(); core.dispose(); core.stats()
 *
 * The model is fp16 33.7 MB Real-ESRGAN x4. Input [1,3,H,W], output [1,3,4H,4W].
 */
(function (global) {
    'use strict';

    var WORKER_URL     = (typeof window!=='undefined'&&window.__jsSrc?window.__jsSrc('/js/ai-upscale-worker.js'):'/js/ai-upscale-worker.js');
    var ORT_URL        = '/vendor/onnxruntime/ort.min.js';
    var ORT_BLOB_TIMEOUT_MS = 25000;
    var S = global.AIUpscaleShared || {};

    // Pre-fetch ort.min.js (~350 KB) on the MAIN thread, build a Blob
    // URL, hand it to the worker via importScripts. Apollo's `c2f246d5d`
    // pattern: importScripts on a Blob URL is a LOCAL read — it cannot
    // hang on the network, which closed ~94% of session-init timeouts in
    // their cohort. The 25 s AbortSignal protects against pathological
    // network states the SW cache hasn't seen yet.
    //
    // Module-scoped cache so the FIRST file pick pays the prefetch
    // cost; subsequent retries / multi-batch uploads reuse the Blob URL.
    var _ortBlobUrlPromise = null;
    function _prefetchOrtBlob() {
        if (_ortBlobUrlPromise) return _ortBlobUrlPromise;
        _ortBlobUrlPromise = (function () {
            // AbortSignal.timeout polyfill for older Safari.
            var abortCtl = typeof AbortController === 'function' ? new AbortController() : null;
            var timer = abortCtl ? setTimeout(function () {
                try { abortCtl.abort(new DOMException('ort prefetch timed out', 'TimeoutError')); }
                catch (_) { try { abortCtl.abort(); } catch (_) {} }
            }, ORT_BLOB_TIMEOUT_MS) : null;
            return fetch(ORT_URL, abortCtl ? { signal: abortCtl.signal } : {})
                .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.blob(); })
                .then(function (blob) {
                    if (timer) clearTimeout(timer);
                    // text/javascript MIME on the Blob so importScripts accepts it.
                    var typed = blob.type ? blob : new Blob([blob], { type: 'text/javascript' });
                    return URL.createObjectURL(typed);
                })
                .catch(function (err) {
                    if (timer) clearTimeout(timer);
                    _ortBlobUrlPromise = null;  // allow retry on next init
                    // Re-throw so the caller can fall back to direct URL.
                    throw err;
                });
        })();
        return _ortBlobUrlPromise;
    }

    // ── Capability check — synchronous, cheap, main-thread (UI gating) ──
    function checkCapabilities() {
        if (typeof WebAssembly === 'undefined') {
            return { supported: false, provider: null, reason: 'no_wasm' };
        }
        var simd = false;
        try {
            simd = WebAssembly.validate(new Uint8Array([
                0,97,115,109,1,0,0,0,1,4,1,96,0,0,3,2,1,0,10,9,1,7,0,
                65,0,253,15,26,11
            ]));
        } catch (e) { simd = false; }

        var devMem = navigator.deviceMemory || 0;
        var cores = navigator.hardwareConcurrency || 0;
        var hasWebGPU = !!(navigator.gpu && typeof navigator.gpu.requestAdapter === 'function');

        // Wave 24 (2026-07-15): `devMem < 4 → unsupported` used to be a
        // blanket refusal. Measured on the real 2-GB-user file shape
        // (324×720 / 9 fps / 18 s, 162 frames): the pipeline is chunked
        // (≤5 s of frames staged in MEMFS at once) and tile-based
        // (inference memory bounded by tile size, not input size) — peak
        // JS heap 75 MB, FLAT across the whole job. The per-INPUT cost is
        // what matters, so devices reporting 2-3 GB now pass with an
        // advisory `lowMem: true` and consumers gate by source area
        // (LOWMEM_SRC_PX) + clamp the chunk window. Devices reporting
        // ≤ 1 GB stay hard-blocked: model (67 MB) + ORT session + FFmpeg
        // WASM alone overflow a 1-GB tab budget regardless of input.
        if (devMem && devMem <= 1) {
            return { supported: false, provider: null, reason: 'low_mem',
                     simd: simd, webgpu: hasWebGPU, devMem: devMem, cores: cores };
        }
        var lowMem = !!(devMem && devMem < 4);
        if (cores && cores < 4) {
            return { supported: false, provider: null, reason: 'low_cores',
                     simd: simd, webgpu: hasWebGPU, devMem: devMem, cores: cores };
        }
        // AI upscale is WebGPU-ONLY — but for SPEED, not for correctness.
        //
        // ⚠️ Here stood: "ORT's WASM CPU backend cannot run the fp16 model —
        // every inference returns an all-zero tensor, i.e. a black video
        // (proven 2026-05-17)". That measurement outlived its subject: the
        // shipped weights are `realesrgan-x4-fp32.onnx`, and re-measured
        // 2026-08-19 the WASM backend returns a perfectly normal picture
        // (range [-0.016, 1.089], mean 0.50, 100% non-zero). Nothing black.
        //
        // What kills the CPU path is the clock. Measured on a 32-core desktop
        // with SharedArrayBuffer enabled (i.e. the best case cross-origin
        // isolation could ever buy us), one 64×64 tile:
        //     1 thread 8070 ms · 4 threads 2950 ms · 8 threads 1850 ms
        // Live WebGPU p50 for comparison: fast desktop 262 ms, slow desktop
        // 2097 ms. So eight threads on a 32-core machine only match the
        // SLOWEST WebGPU desktop we know of — and that device is already the
        // source of the "72 hours" complaints (a 15 s 1080p ×2 clip is
        // ~81 000 tiles). Threads make the refusal slower, not wrong.
        //
        // Hence: still refuse without WebGPU, and never re-derive this from
        // the comment alone — re-measure (see the memory note for the 30-second
        // probe) before believing either the old claim or this one.
        if (!hasWebGPU) {
            return { supported: false, provider: null, reason: 'no_webgpu',
                     simd: simd, webgpu: false, devMem: devMem, cores: cores, lowMem: lowMem };
        }
        return {
            supported: true, provider: 'webgpu',
            reason: null, simd: simd, webgpu: true, devMem: devMem, cores: cores,
            lowMem: lowMem
        };
    }

    // Wave 24: max SOURCE area (px) for AI upscale on lowMem (2-3 GB)
    // devices — 640×480. Measured safe end-to-end at 324×720 (233 kpx);
    // 307 kpx keeps the 4× output ≤ ~4.9 MPx so the per-chunk PNG staging
    // + WASM x264 working set stay inside a 2-GB device's tab budget.
    // Shared by enhance-video's toggle gate and ai-upscale-pipeline's
    // run() guard so the two can't drift.
    var LOWMEM_SRC_PX = 640 * 480;

    // SHA-256 of the fp32 Real-ESRGAN x4 model (realesrgan-x4-fp32.onnx,
    // switched 2026-05-17 — the fp16 export is unrunnable, see config/assets.php).
    // Catches a corrupt download in any cache BEFORE ORT emits an opaque error.
    var EXPECTED_MODEL_SHA = 'fa18ce70de3a55f3149d0cc898d335d2d69fca29edc0692cb362c856b2942c3f';

    function verifyModelSha(buf, expected) {
        expected = expected || EXPECTED_MODEL_SHA;
        // SubtleCrypto needs a secure context. If absent (file://, ancient
        // Safari) skip rather than refuse — worst case is trusting the bytes.
        if (typeof crypto === 'undefined' || !crypto.subtle || !crypto.subtle.digest) {
            return Promise.resolve({ verified: false, reason: 'no_subtle' });
        }
        return crypto.subtle.digest('SHA-256', buf).then(function (hashBuf) {
            var got = (S.bytesToHex || function(){return '';})(hashBuf);
            if (got === expected) return { verified: true, sha: got };
            return { verified: false, reason: 'mismatch', sha: got };
        });
    }

    function fetchModelWithProgress(urls, onProgress, signal) {
        var list = Array.isArray(urls) ? urls.slice() : [urls];
        function aborted() { return !!(signal && signal.aborted); }
        function makeAbortError() {
            var e = new Error('aborted'); e.name = 'AbortError'; return e;
        }
        function tryAt(i) {
            if (aborted()) return Promise.reject(makeAbortError());
            if (i >= list.length) return Promise.reject(new Error('all model URLs failed'));
            // Pass the signal to fetch so the underlying request aborts
            // as soon as user cancels — previously a click on Cancel
            // during the 67 MB download just sat there until done.
            return fetch(list[i], signal ? { signal: signal } : undefined).then(function (resp) {
                if (!resp.ok) throw new Error('HTTP ' + resp.status);
                var total = parseInt(resp.headers.get('Content-Length') || '0', 10);
                if (!resp.body) return resp.arrayBuffer();
                var reader = resp.body.getReader();
                var chunks = [], received = 0;
                function pump() {
                    if (aborted()) {
                        // Release the underlying connection promptly.
                        try { reader.cancel(); } catch (_) {}
                        return Promise.reject(makeAbortError());
                    }
                    return reader.read().then(function (r) {
                        if (r.done) {
                            var buf = new Uint8Array(received), off = 0;
                            for (var k = 0; k < chunks.length; k++) { buf.set(chunks[k], off); off += chunks[k].length; }
                            return buf.buffer;
                        }
                        chunks.push(r.value);
                        received += r.value.length;
                        if (onProgress) try { onProgress(received, total); } catch (_) {}
                        return pump();
                    });
                }
                return pump();
            }).catch(function (err) {
                // Don't fall through to the next URL on a real abort —
                // user wants out, not a retry on the fallback CDN.
                if (err && err.name === 'AbortError') throw err;
                return tryAt(i + 1);
            });
        }
        return tryAt(0);
    }

    function AIUpscaleCore(opts) {
        opts = opts || {};
        this.modelUrls = opts.modelUrls;
        /* SHA и тайл — СВОЙСТВА ВЫБРАННОЙ МОДЕЛИ, а не модуля. До 26.08 модель
           была одна, и оба жили константами; с реестром
           (config/upscale-models.php) их приносит вызывающий. Без этого
           переключение модели ломало бы проверку целостности: чужая сумма не
           сойдётся, и загрузка честной модели выглядела бы как испорченный
           кэш. Пусто → прежние умолчания, поэтому старые вызовы живы. */
        this.expectedSha = opts.expectedSha || null;
        this.modelTile = opts.modelTile || 0;
        /* How long the first real inference (the kernel probe) may take before
           the worker calls the GPU broken. One wall for every device used to be
           15 s, and on phones that wall WAS the failure: prod RU, 30 d,
           /photo-enhancer — 234 mobile sessions ended in
           «kernel probe failed — webgpu-probe-timeout» with kernel_probe_ms
           pinned at 15 0xx, i.e. the GPU was still compiling when we gave up
           (mobile session.create p50 6.9 s on top). 233 of the 247 mobile
           errors were this one line. Phones get twice the time; a genuinely
           broken driver still fails, half a minute later. */
        this.probeBudgetMs = opts.probeBudgetMs || (AIUpscaleCore.isPhone() ? AIUpscaleCore.PHONE_PROBE_BUDGET_MS : 0);
        // Which tool this instance belongs to. The worker's telemetry used to
        // be hardcoded to 'video-upscaler', so every model-load / kernel-probe
        // event from photo-enhancer, photo-restore and enhance-video landed in
        // the video tool's cohort — three tools invisible, one tool's numbers
        // inflated. Default keeps the historical slug for callers that predate
        // the option.
        this.slug = opts.slug || 'video-upscaler';
        /* Where the ladder may step down when the GPU answers the known question
           WRONG (OrtEpVerify): a photo is a few hundred tiles behind an honest
           ETA — the CPU rung is a real option there; a video is hours — the page
           says why instead. Default off: a wrong GPU then fails typed
           (`ai_webgpu_output_wrong`), never striped. */
        this.allowWasm = !!opts.allowWasm;
        // The verifier's versioned URL for the worker (same file the page loads).
        this.verifyUrl = opts.verifyUrl || ((typeof window !== 'undefined' && typeof window.__jsSrc === 'function') ? window.__jsSrc('/js/ort-ep-verify.js') : null);
        this.verified = false; this.unverified = false; this.katDb = null;
        this.aiVerdictKey = null; this.adapter = null;
        this.onDowngraded = null;   // page hook: the GPU went wrong mid-run, the run continues on WASM
        this.tileSize = opts.tileSize || (
            /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? 192 : 256
        );
        this.overlap = opts.overlap || 16;
        this.scale = 4;                 // Real-ESRGAN x4 fixed
        this.session = null;            // sentinel — set truthy once worker is ready
        this.provider = null;
        this._cancelled = false;
        this._tilesProcessed = 0;
        this._tileMs = 0;
        this._worker = null;
        this._reqId = 0;
        this._pending = Object.create(null);   // upscale request id → {resolve,reject,onTileProgress}
        // Owned AbortController fed into the model fetch + (future) any other
        // async I/O that needs to die on requestCancel(). Aborting here is
        // a hard transport-level signal; the worker's chunk-loop _cancelled
        // flag is the cooperative signal — both serve different stages.
        this._fetchAbort = (typeof AbortController === 'function') ? new AbortController() : null;
    }

    // Spawn the worker, wire its message router, hand it the verified model
    // bytes, and resolve when it reports the session is compiled and ready.
    AIUpscaleCore.prototype._spawnAndInit = function (modelBytes, caps) {
        var self = this;
        return new Promise(function (resolve, reject) {
            var w;
            try { w = new Worker(WORKER_URL); }
            catch (e) {
                reject(Object.assign(new Error('AI worker spawn failed: ' + (e && e.message || e)),
                                     { code: 'worker_spawn_failed' }));
                return;
            }
            self._worker = w;
            self._initReject = reject;
            w.onerror = function (ev) {
                var err = new Error('AI worker error: ' + (ev && ev.message || 'unknown'));
                if (self._initReject) { self._initReject(err); self._initReject = null; }
                // Fail any in-flight upscale requests too.
                Object.keys(self._pending).forEach(function (id) {
                    var p = self._pending[id]; delete self._pending[id];
                    try { p.reject(err); } catch (_) {}
                });
            };
            w.onmessage = function (ev) {
                var m = ev.data || {};
                if (m.type === 'telemetry') {
                    try {
                        window.__ae && window.__ae('feature_use', {
                            slug: self.slug, action: m.action,
                            err: m.err,
                            session_create_ms: m.sessionCreateMs,
                            kernel_probe_ms: m.kernelProbeMs
                        });
                    } catch (_) {}
                } else if (m.type === 'verdict') {
                    // One rung's known-answer result (init / recheck / downgrade).
                    // Only a WRONG answer on a non-CPU rung is remembered — a thrown
                    // test or a cached skip is not a verdict (OrtEpVerify rules).
                    var v = m.verdict || {};
                    var V = window.OrtEpVerify;
                    try {
                        var flat = (V && V.telemetryOf) ? V.telemetryOf(v, self.adapter) : { ep: v.provider, ok: v.ok ? 1 : 0, psnr: v.psnr };
                        flat.slug = self.slug; flat.action = 'ai_ep_verdict'; flat.phase = m.phase || v.phase || 'init';
                        flat.tile = m.tile || null;
                        window.__ae && window.__ae('feature_use', flat);
                    } catch (_) {}
                    try {
                        if (V && self.aiVerdictKey && !v.ok && !v.error && !v.skipped && v.provider && v.provider !== 'wasm') {
                            V.remember(self.aiVerdictKey, v.provider, { kind: 'upscale-kat', psnr: v.psnr, period: v.sig && v.sig.period });
                        }
                    } catch (_) {}
                } else if (m.type === 'downgraded') {
                    self.provider = m.to;
                    if (m.wasmMs > 0) self.tileMs = m.wasmMs;   // the CPU rung's own measured tile — the page re-quotes the redo from it
                    try {
                        window.__ae && window.__ae('feature_use', { slug: self.slug, action: 'ai_gpu_corrupt_downgrade',
                            from: m.from, to: m.to, psnr: m.verdict && m.verdict.psnr, period: m.verdict && m.verdict.sig && m.verdict.sig.period });
                    } catch (_) {}
                    try { if (typeof self.onDowngraded === 'function') self.onDowngraded(m); } catch (_) {}
                } else if (m.type === 'ready') {
                    self.session = { ready: true };   // truthy sentinel for the not-initialized guard
                    self.provider = m.provider;
                    self.verified = !!m.verified; self.unverified = !!m.unverified; self.katDb = (m.kat_db == null) ? null : m.kat_db;
                    // The tile time the ADOPTED rung just measured (its known-answer tile) and the tile it cuts with:
                    // the page quotes this run from them before the first tile (estimateRunMs).
                    self.tileMs = (m.tileMsMeasured > 0) ? m.tileMsMeasured : null; self.modelTileUsed = (m.modelTile | 0) || 0;
                    try {
                        window.__ae && window.__ae('feature_use', { slug: self.slug, action: 'ai_upscale_ready', provider: m.provider,
                            verified: m.verified ? 1 : 0, unverified: m.unverified ? 1 : 0, kat_db: m.kat_db, kat_ms: m.kat_ms, rungs: m.rungs,
                            tile: m.modelTile, tile_ms: m.tileMsMeasured, session_create_ms: m.sessionCreateMs, kernel_probe_ms: m.kernelProbeMs });
                    } catch (_) {}
                    self.modelInputDtype = m.dtype;
                    self.sessionCreateMs = m.sessionCreateMs;
                    self.kernelProbeMs = m.kernelProbeMs;
                    // Steady-state per-tile inference cost measured on THIS
                    // GPU (2nd warm probe run in the worker). Consumers use
                    // it to calibrate ETA before the first real frame.
                    self.tileMsMeasured = m.tileMsMeasured || 0;
                    // The tile the worker will REALLY cut with: the registry's
                    // ask, or 64 after the probe rejected it (`ai_tile_fallback`).
                    // Until 2026-09-16 this stayed inside the worker, so a page
                    // that asked for 128 and got 64 quoted 4× too few tiles.
                    self.modelTile = (m.modelTile | 0) || self.modelTile || 64;
                    self.tileFellBack = !!m.tileFellBack;
                    if (self._initReject) { self._initReject = null; }
                    resolve(self);
                } else if (m.type === 'error' && m.id == null) {
                    // Stash timing fields on the error so the catch in
                    // ai-upscale-pipeline can include them in
                    // ai_upscale_failed telemetry — closes the audit
                    // gap "we only know failed > timeout, not whether
                    // create or probe was slow".
                    self._lastFailureTimings = {
                        sessionCreateMs: m.sessionCreateMs,
                        kernelProbeMs: m.kernelProbeMs
                    };
                    // init-phase error
                    if (self._initReject) { self._initReject = null; }
                    reject(Object.assign(new Error(m.message || 'AI init failed'), {
                        code: m.code,
                        sessionCreateMs: m.sessionCreateMs,
                        kernelProbeMs: m.kernelProbeMs
                    }));
                } else if (m.type === 'tileProgress') {
                    var pe = self._pending[m.id];
                    self._tilesProcessed++;
                    self._tileMs += m.ms || 0;
                    if (pe) try { pe.onTileProgress({ done: m.done, total: m.total, ms: m.ms }); } catch (_) {}
                } else if (m.type === 'result') {
                    var pr = self._pending[m.id];
                    if (pr) {
                        delete self._pending[m.id];
                        try {
                            var px = new Uint8ClampedArray(m.rgba);
                            var c = document.createElement('canvas');
                            c.width = m.width; c.height = m.height;
                            c.getContext('2d').putImageData(new ImageData(px, m.width, m.height), 0, 0);
                            pr.resolve(c);
                        } catch (e) { pr.reject(e); }
                    }
                } else if (m.type === 'error' && m.id != null) {
                    var pf = self._pending[m.id];
                    if (pf) {
                        delete self._pending[m.id];
                        pf.reject(Object.assign(new Error(m.message || 'AI upscale failed'), { code: m.code }));
                    }
                }
            };
            var dtypeHint = /(fp16|float16)/i.test(
                (self.modelUrls && [].concat(self.modelUrls).join(' ')) || ''
            ) ? 'float16' : null;
            // Build the absolute wasmBase up-front (origin + path) — the
            // worker needs an ABSOLUTE URL once we load ort.min.js from a
            // Blob URL, because ORT's internal `import()` of jsep /
            // threaded WASM helpers resolves origin-relative specifiers
            // against the SCRIPT origin (Blob URL → no helper siblings).
            var wasmBase = (typeof window !== 'undefined' && window.location)
                ? window.location.origin + '/vendor/onnxruntime/'
                : '/vendor/onnxruntime/';
            // Try to prefetch ort.min.js as Blob URL — if it succeeds,
            // pass it to the worker so importScripts is a LOCAL read.
            // If it fails (timeout, CSP, network) the worker falls back
            // to the canonical URL and works the same as before.
            _prefetchOrtBlob().catch(function () { return null; }).then(async function (blobUrl) {
                // The 30-day memory of a GPU that answered the known question wrong
                // (OrtEpVerify): keyed by model + adapter + browser, so a driver
                // update or another model gets a fresh trial. Lives on the page —
                // the worker has no localStorage.
                var rejected = { webgpu: false };
                try {
                    var V = window.OrtEpVerify;
                    if (V && caps && caps.webgpu) {
                        try { self.adapter = V.adapterIdentity ? await V.adapterIdentity() : null; } catch (_) { self.adapter = null; }
                        var modelName = String([].concat(self.modelUrls || [])[0] || 'upscale').split('?')[0].split('/').pop();
                        self.aiVerdictKey = V.verdictKey(['upscale', modelName,
                            (self.adapter && (self.adapter.vendor + '/' + self.adapter.architecture)) || 'gpu?',
                            (navigator.userAgent || '').slice(0, 40)]);
                        rejected.webgpu = !!V.isRejected(self.aiVerdictKey, 'webgpu');
                        if (rejected.webgpu) { try { window.__ae && window.__ae('feature_use', { slug: self.slug, action: 'ai_ep_skip', ep: 'webgpu' }); } catch (_) {} }
                    }
                } catch (_) {}
                // Transfer the model ArrayBuffer — zero-copy hand-off to the worker.
                w.postMessage({
                    cmd: 'init', modelBytes: modelBytes,
                    webgpu: !!(caps && caps.webgpu), dtypeHint: dtypeHint,
                    verifyUrl: self.verifyUrl, rejected: rejected, allowWasm: !!self.allowWasm,
                    katFail: (typeof window !== 'undefined' && window.__PE_KAT_FAIL) || null,
                    recheckEvery: (typeof window !== 'undefined' && (window.__PE_RECHECK_EVERY | 0)) || 0,
                    probeBudgetOnce: (typeof window !== 'undefined' && (window.__PE_PROBE_BUDGET_ONCE_MS | 0)) || 0,
                    // Просьба, не приказ: воркер проверит её пробой и при
                    // отказе откатится на 64, сообщив об этом в `ready`.
                    modelTile: self.modelTile || 0,
                    probeBudgetMs: self.probeBudgetMs || 0,
                    ortBlobUrl: blobUrl || null,
                    wasmBase: wasmBase
                }, [modelBytes]);
            });
        });
    };

    AIUpscaleCore.prototype.init = function (cb) {
        cb = cb || {};
        var self = this;
        var onProgress = cb.onProgress || function () {};
        var caps = checkCapabilities();
        if (!caps.supported) {
            return Promise.reject(Object.assign(new Error('AI upscale not supported on this device'),
                                                { code: 'unsupported', reason: caps.reason }));
        }
        return fetchModelWithProgress(self.modelUrls, function (loaded, total) {
            onProgress({ phase: 'download', loaded: loaded, total: total });
        }, self._fetchAbort ? self._fetchAbort.signal : null).then(function (buf) {
            onProgress({ phase: 'verify' });
            // ⚠️ Сверяем с суммой ВЫБРАННОЙ модели, а не с модульной константой:
            // иначе честная загрузка любой другой записи реестра выглядит как
            // испорченный кэш (поймано сторожем upscaler-every-model-runs).
            return verifyModelSha(buf, self.expectedSha).then(function (v) {
                self._verified = v;
                if (v.reason === 'mismatch') {
                    // Cache-poisoning hit — fail loudly with a .code the UI can
                    // act on (refresh-and-retry) instead of an opaque LinkError.
                    throw Object.assign(
                        new Error('AI model checksum mismatch (got ' + v.sha + ')'),
                        { code: 'model_corrupt', sha: v.sha }
                    );
                }
                return buf;
            });
        }).then(function (buf) {
            onProgress({ phase: 'compile', verified: !!(self._verified && self._verified.verified) });
            return self._spawnAndInit(buf, caps);
        }).then(function () {
            onProgress({ phase: 'ready', provider: self.provider, dtype: self.modelInputDtype });
            return self;
        });
    };

    AIUpscaleCore.prototype.upscaleFrame = function (inputCanvas, opts) {
        opts = opts || {};
        var self = this;
        var onTileProgress = opts.onTileProgress || function () {};
        var iw = inputCanvas.width, ih = inputCanvas.height;
        // Degenerate inputs — Real-ESRGAN needs ≥16 px. Guard runs on the main
        // thread (cheap) before any worker round-trip.
        if (!iw || !ih || iw < 16 || ih < 16) {
            return Promise.reject(Object.assign(
                new Error('frame too small for AI upscale (' + iw + 'x' + ih + ')'),
                { code: 'frame_too_small' }
            ));
        }
        if (!this.session) return Promise.reject(new Error('not initialized'));
        if (this._cancelled) return Promise.reject(new Error('cancelled'));
        if (!this._worker) return Promise.reject(new Error('not initialized'));

        var ctx = inputCanvas.getContext('2d', { willReadFrequently: true });
        var img = ctx.getImageData(0, 0, iw, ih);
        /* opts.opaque — вход непрозрачен по природе (кадр видео). Прочитанный
           прозрачным насквозь, он не чёрная сцена, а холст, стёртый сбросом
           видеокарты: модель увеличила бы пустоту, и в ролик ушёл бы чёрный кадр.
           Отказ типизирован — вызывающий перерисует кадр из своего источника.
           Без opts.opaque не проверяем: у фото бывает настоящая прозрачность. */
        if (opts.opaque && global.PixelProof && global.PixelProof.emptied && global.PixelProof.emptied(img)) {
            return Promise.reject(Object.assign(new Error('input frame read back empty (GPU reset)'), { code: 'input_lost' }));
        }
        var id = ++self._reqId;
        return new Promise(function (resolve, reject) {
            self._pending[id] = { resolve: resolve, reject: reject, onTileProgress: onTileProgress };
            var buf = img.data.buffer;   // transfer — detaches img, which we don't reuse
            self._worker.postMessage({
                cmd: 'upscale', id: id, rgba: buf, width: iw, height: ih,
                tileSize: self.tileSize, overlap: self.overlap
            }, [buf]);
        });
    };

    AIUpscaleCore.prototype.requestCancel = function () {
        this._cancelled = true;
        // Abort any in-flight model fetch — without this, clicking Cancel
        // during the 67 MB download just sat there until done.
        if (this._fetchAbort) { try { this._fetchAbort.abort(); } catch (_) {} }
        if (this._worker) try { this._worker.postMessage({ cmd: 'cancel' }); } catch (_) {}
    };

    AIUpscaleCore.prototype.dispose = function () {
        this._cancelled = true;
        if (this._worker) { try { this._worker.terminate(); } catch (_) {} this._worker = null; }
        // Reject anything still in flight so callers don't hang.
        var self = this;
        Object.keys(this._pending).forEach(function (id) {
            var p = self._pending[id]; delete self._pending[id];
            try { p.reject(new Error('disposed')); } catch (_) {}
        });
        this.session = null;
    };

    AIUpscaleCore.prototype.stats = function () {
        return {
            tiles: this._tilesProcessed,
            avg_tile_ms: this._tilesProcessed ? Math.round(this._tileMs / this._tilesProcessed) : 0,
            provider: this.provider,
            tile_size: this.tileSize
        };
    };

    AIUpscaleCore.checkCapabilities = checkCapabilities;
    AIUpscaleCore.LOWMEM_SRC_PX = LOWMEM_SRC_PX;

    // Stash for the latest probe's adapter info — used by pipeline.js to
    // attach `gpu_vendor` / `gpu_architecture` / `gpu_is_fallback` to
    // every `ai_upscale_start` and `ai_upscale_failed` event. Pre-fix the
    // failure payload was missing adapter info entirely, so we couldn't
    // crosstab "Intel iGPU" vs "NVIDIA" failure shares — the single
    // biggest diagnostic gap surfaced by the 2026-06-08 audit.
    AIUpscaleCore.lastAdapterInfo = null;

    /* ── HOW LONG WILL THIS TAKE ON THIS DEVICE ─────────────────────────────
       Born from comment #1645 (12.09.2026, «больше 10 часов "улучшалось"
       3-х минутное видео»). /enhance-video quoted the AI lane from a flat
       constant — 3600 ms per 1080p frame, inherited from /video-upscaler's
       heuristic and never measured here — while the run's real cost is
       tiles × per-tile inference, and BOTH factors vary by an order of
       magnitude across devices. Measured on prod (213 completed AI runs,
       90 d): per-tile p50 89 ms, p90 285, max 3563 — so the same clip is
       20 minutes on one machine and 10 hours on another, and the constant
       could not know which.

       Two facts belong here rather than in a blade:
         · the tile COUNT, which is not a formula to re-derive but the
           worker's own `planTiles` — the same function that will do the
           cutting (verified against prod: a 320×180 inference frame plans
           28 tiles, and 28 is the modal bucket of the 213 real runs);
         · the per-tile SPEED of this device, which the worker already
           measures at init (`tileMsMeasured`, a second warm probe run) and
           which nobody remembered between visits, so every first run on a
           slow GPU re-learned it the expensive way.

       Not a second estimator: /video-upscaler keeps its own per-MODEL table
       (it offers several models); this is the shared floor both can stand on
       and enhance-video's only source. */
    var TILE_MS_KEY = 'aiTileMsObserved';
    var TILE_MS_TTL_MS = 90 * 24 * 3600 * 1000;   // a GPU does not change often; a machine might
    /* How much slower a MEDIAN REAL VISITOR is than the machine the registry's
       `ms_per_tile_ref` numbers were measured on (RTX 4090). Prod, 90 d,
       /enhance-video foreground AI runs: per-tile p50 89 ms with x4plus at
       tile 64, against the registry's 65.9 ms for that same pair — so 1.35.
       ⚠️ A ratio, not a constant per model: `ms_per_tile_ref` already carries
       what the model costs, and a default written in milliseconds would go
       silently wrong the day the registry's default model changes (it just
       did — this tool moved off x4plus on 12.09.2026). The tail is what
       actually hurts and no default can express it: p90 is 285 ms and the max
       3563, which is why the measured reading always wins over this. */
    AIUpscaleCore.FLEET_SLOWDOWN_VS_REF = 1.35;
    /* The same ratio for a PHONE. Prod RU, 30 d, /photo-enhancer, 200 completed
       mobile runs of x4plus at tile 64, inference only (run time minus the
       session's own model-load time, divided by the tiles the worker planned
       for the fed size): per-tile p25 562 ms, p50 1145, p75 2253, p90 4796 —
       against the registry's 65.9 ms on the reference GPU. 17× at the median;
       the p90 phone is 73× and no default can describe it, which is why the
       measured reading always replaces this after the first probe. Safari
       (iPhone) averages ~1.2 s a tile, Chrome on Android ~4 s.
       Cross-checked on the Compact model: /enhance-video `ai_gpu_calibrated`,
       27 phones in the four days after it moved to `balanced` — per 128-px
       tile 86…3341 ms, median 411, against the registry's 28.2 → 14.6×. Same
       class of number from a different model and a different tool, so the
       ratio is the device, not the network; 17 keeps the quote on the safe
       (long) side until the device has been timed. */
    AIUpscaleCore.PHONE_SLOWDOWN_VS_REF = 17;
    AIUpscaleCore.PHONE_PROBE_BUDGET_MS = 30000;
    AIUpscaleCore.isPhone = function () {
        return /Mobi|Android|iPhone|iPad/i.test((typeof navigator !== 'undefined' && navigator.userAgent) || '');
    };
    // Fallback per-tile cost when neither a measurement nor a registry
    // reference is available (x4plus at tile 64, fleet p50).
    AIUpscaleCore.DEFAULT_TILE_MS = 89;
    // The tile the worker actually cuts with when the page asks for nothing.
    // ⚠️ NOT `core.tileSize`: the worker ignores the per-request tileSize and
    // uses its init-time MODEL_TILE, so an estimate built on the requested
    // 256 would count 16× too few tiles.
    AIUpscaleCore.MODEL_TILE_DEFAULT = 64;
    AIUpscaleCore.TILE_OVERLAP = 16;

    /** Exact tiles for one frame at the INFERENCE size, via the worker's own planner. */
    AIUpscaleCore.tilesPerFrame = function (inferW, inferH, modelTile) {
        var S = (typeof window !== 'undefined') && window.AIUpscaleShared;
        var t = (modelTile | 0) || AIUpscaleCore.MODEL_TILE_DEFAULT;
        if (!S || typeof S.planTiles !== 'function' || !(inferW > 0) || !(inferH > 0)) return 0;
        try { return S.planTiles(inferW, inferH, t, AIUpscaleCore.TILE_OVERLAP).length; }
        catch (_) { return 0; }
    };

    /**
     * Milliseconds THIS run would take on the rung the worker adopted, from the tile the worker
     * timed a moment ago (its known-answer tile) and the worker's own tile planner. 0 = unknown.
     * Why it exists: the CPU rung is honest only when it is affordable. Measured 2026-09-16 on the
     * RTX 4060 box: x4plus at tile 64 costs 6-12 s per tile on WASM once the wasm is compiled (32-38 s
     * for the cold first run) against 67 ms on WebGPU, so a 640x426 photo (117 tiles) is ~18 minutes
     * there and a phone photo is hours. The page decides the ceiling; this only says the number.
     */
    AIUpscaleCore.prototype.estimateRunMs = function (inferW, inferH) {
        var n = AIUpscaleCore.tilesPerFrame(inferW, inferH, this.modelTileUsed || 0);
        return (n > 0 && this.tileMs > 0) ? n * this.tileMs : 0;
    };

    /** Remember what this device measured, so the NEXT estimate is honest before any work. */
    AIUpscaleCore.rememberTileMs = function (slug, ms) {
        if (!(ms > 0)) return;
        try {
            window.__toolSettings && window.__toolSettings.save(slug || 'ai-upscale', TILE_MS_KEY,
                JSON.stringify({ ms: Math.round(ms), at: Date.now() }));
        } catch (_) {}
    };
    /** The measured per-tile cost, or 0 when this device has never been timed (or the reading is stale). */
    AIUpscaleCore.recallTileMs = function (slug) {
        try {
            var raw = window.__toolSettings && window.__toolSettings.load(slug || 'ai-upscale', TILE_MS_KEY, null);
            if (!raw) return 0;
            var o = (typeof raw === 'string') ? JSON.parse(raw) : raw;
            if (!o || !(o.ms > 0)) return 0;
            if (o.at && (Date.now() - o.at) > TILE_MS_TTL_MS) return 0;
            return o.ms;
        } catch (_) { return 0; }
    };

    /**
     * Seconds this AI run will take on THIS device.
     * @param {{inferW:number, inferH:number, frames:number, tileMs?:number, modelTile?:number, slug?:string}} o
     * @returns {{sec:number, tiles:number, tileMs:number, measured:boolean}} sec 0 when it cannot be answered.
     */
    AIUpscaleCore.estimateRun = function (o) {
        o = o || {};
        var tiles = AIUpscaleCore.tilesPerFrame(o.inferW, o.inferH, o.modelTile);
        var frames = o.frames | 0;
        var measuredMs = (o.tileMs > 0) ? o.tileMs : AIUpscaleCore.recallTileMs(o.slug);
        var slow = AIUpscaleCore.isPhone() ? AIUpscaleCore.PHONE_SLOWDOWN_VS_REF : AIUpscaleCore.FLEET_SLOWDOWN_VS_REF;
        var fallbackMs = (o.msPerTileRef > 0)
            ? o.msPerTileRef * slow
            : AIUpscaleCore.DEFAULT_TILE_MS * (AIUpscaleCore.isPhone() ? AIUpscaleCore.PHONE_SLOWDOWN_VS_REF / AIUpscaleCore.FLEET_SLOWDOWN_VS_REF : 1);
        var tileMs = measuredMs > 0 ? measuredMs : fallbackMs;
        if (!tiles || !frames) return { sec: 0, tiles: tiles, tileMs: tileMs, measured: measuredMs > 0 };
        /* A frame costs INFERENCE plus a fixed amount of per-frame handling —
           PNG decode, canvas draw, PNG encode, write back into the FS — and the
           second term is not a rounding error.
           ⚠️ It used to be modelled as ×1.07, which came from prod under the OLD
           model: 7225 ms of tile inference in a 7764 ms frame, so handling was
           7 % and a multiplier was fair. Moving to the registry's Compact model
           cut inference ~19× and the SAME handling became the larger half —
           measured on this machine, 640×360 → 720p: 6 tiles × 35 ms = 210 ms of
           inference inside a 394 ms frame, i.e. 184 ms of handling, 47 %. A
           multiplier tuned on the old ratio would now under-quote by about 2×,
           and under-quoting is the exact failure comment #1645 reported.
           So handling is additive and scales with the OUTPUT frame, which is
           what gets PNG-decoded, drawn, PNG-encoded and written back.

           Both constants come from a two-point fit on the real page rather
           than from a guess — same machine, same file, same settings, only the
           length changed, so the slope and the intercept separate cleanly:
               180 frames → 223 s
               300 frames → 290 s
             slope 558 ms/frame · intercept 123 s
           At the probe's 22 ms/tile and 6 tiles, inference is 132 ms of that
           558, leaving 426 ms of handling on a 0.92 Mpx output → 460 ms/Mpx.
           ⚠️ A first cut on ONE machine; the `ai_gpu_calibrated` event now
           carries the quote and `tool_complete` the real duration, so the
           fleet can correct both without another bench. */
        var outMpx = (o.inferW * o.inferH * 16) / 1e6;      // the model is ×4 per side
        var sec = (frames * (tiles * tileMs + outMpx * 460)) / 1000;
        /* Model fetch (cached after the first visit), ORT's WebGPU shader
           compile, FFmpeg-WASM init. Measured as the intercept above; it is
           the whole cost of a very short clip and noise on a long one. */
        sec += 120;
        return { sec: Math.round(sec), tiles: tiles, tileMs: tileMs, measured: measuredMs > 0 };
    };

    // Live WebGPU probe — minimal compute-shader dispatch that exercises the
    // shader-compile pipeline. Catches ~70 % of real driver compile failures
    // without the 30 MB model download (~150 ms). Stays on the main thread:
    // it's a pre-flight UI gate, called before the worker exists.
    /* Why the probe fails is the WHOLE question for this cohort, and until
       2026-09-17 it answered `false` with no reason at all: 896 sessions in 30
       days on RU hit `webgpu_probe_failed` (three quarters of every unsupported
       verdict) and we could not say whether the adapter was refused, the device
       request threw, the shader would not compile or the answer came back
       wrong. `lastProbeFail` names the step; the page ships it with cap_probe.
       ⚠️ The probe stays BOOLEAN — four callers depend on that. */
    AIUpscaleCore.lastProbeFail = null;      // {step, err} of the last false
    AIUpscaleCore.lastProbeRetry = 0;        // 1 = the default adapter saved it
    AIUpscaleCore.probe = async function () {
        AIUpscaleCore.lastProbeFail = null;
        AIUpscaleCore.lastProbeRetry = 0;
        var step = 'no_gpu';
        var fail = function (s, err) {
            AIUpscaleCore.lastProbeFail = {
                step: s,
                err: String((err && err.message) || err || '').slice(0, 120)
            };
            return false;
        };
        if (typeof navigator === 'undefined' || !navigator.gpu) return fail('no_gpu');
        var device = null;
        try {
            // powerPreference: 'high-performance' — Real-ESRGAN ×4 is a
            // discrete-GPU-class workload (~200 layers of Conv + LeakyReLU).
            // On hybrid-GPU laptops (NVIDIA dGPU + Intel iGPU) Chrome
            // defaults to the low-power iGPU, which is where cold-compile
            // slowness + 5 s probe budget historically collided.
            // browsers MAY ignore the hint; safe regardless.
            step = 'adapter';
            var adapter = await navigator.gpu.requestAdapter({
                powerPreference: 'high-performance'
            });
            if (!adapter) {
                /* `powerPreference` is a HINT, and a hint that answers null is
                   still a refusal to us. Asking again WITHOUT it costs one call
                   and can only widen what is accepted. Whether it rescues
                   anybody is a question for the field, not for an opinion:
                   `adapter_retry` rides along in cap_probe, so a month from now
                   the number decides whether this branch stays. */
                AIUpscaleCore.lastProbeRetry = 1;
                try { adapter = await navigator.gpu.requestAdapter(); } catch (_) {}
                if (!adapter) return fail('adapter_null');
            }
            // Capture adapter identity for downstream telemetry. `adapter.info`
            // is widely supported per the spec; older Chromium versions
            // expose `adapter.requestAdapterInfo()` instead — try both.
            try {
                var info = adapter.info;
                if (!info && typeof adapter.requestAdapterInfo === 'function') {
                    info = await adapter.requestAdapterInfo();
                }
                AIUpscaleCore.lastAdapterInfo = {
                    vendor: (info && info.vendor) || '',
                    architecture: (info && info.architecture) || '',
                    device: (info && info.device) || '',
                    description: (info && info.description) || '',
                    isFallbackAdapter: !!adapter.isFallbackAdapter
                };
            } catch (_) {
                AIUpscaleCore.lastAdapterInfo = {
                    vendor: '', architecture: '', device: '',
                    description: '', isFallbackAdapter: !!adapter.isFallbackAdapter
                };
            }
            // Fallback-adapter detection — Chrome's SwiftShader / ANGLE
            // CPU emulation. Real-ESRGAN on a fallback adapter would take
            // ~30-60 min per frame, surfacing as a 15 s probe timeout. Treat
            // it as "WebGPU effectively unavailable" up-front so the UI can
            // route the user to the honest "your GPU/driver won't run this"
            // message instead of waiting for the timeout. NOT used as a hard
            // pre-block on Intel iGPU's — that pre-blocking risks false
            // positives on Intel Gen 12+ with recent drivers, where the
            // probe DOES succeed. The 7-day telemetry watch will surface
            // whether explicit pre-blocking is worth adding next cycle.
            if (adapter.isFallbackAdapter) {
                return fail('fallback_adapter');
            }
            step = 'device';
            device = await adapter.requestDevice();
            if (!device) return fail('device_null');
            step = 'shader';
            var shaderCode = '@group(0) @binding(0) var<storage, read> inp : array<f32>;'
                + '@group(0) @binding(1) var<storage, read_write> outp : array<f32>;'
                + '@compute @workgroup_size(8, 8) fn main(@builtin(global_invocation_id) gid : vec3<u32>) {'
                + '  let idx = gid.y * 8u + gid.x;'
                + '  outp[idx] = inp[idx] * 2.0 + 1.0;'
                + '}';
            var module = device.createShaderModule({ code: shaderCode });
            if (typeof module.getCompilationInfo === 'function') {
                var info = await module.getCompilationInfo();
                if (info && info.messages) {
                    for (var i = 0; i < info.messages.length; i++) {
                        if (info.messages[i].type === 'error') {
                            return fail('shader_compile', info.messages[i].message);
                        }
                    }
                }
            }
            step = 'dispatch';
            var inBuf = device.createBuffer({ size: 256, usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST });
            var outBuf = device.createBuffer({ size: 256, usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC });
            var readBuf = device.createBuffer({ size: 256, usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST });
            device.queue.writeBuffer(inBuf, 0, new Float32Array(64).fill(1.0));
            var pipeline = device.createComputePipeline({
                layout: 'auto', compute: { module: module, entryPoint: 'main' }
            });
            var bindGroup = device.createBindGroup({
                layout: pipeline.getBindGroupLayout(0),
                entries: [
                    { binding: 0, resource: { buffer: inBuf } },
                    { binding: 1, resource: { buffer: outBuf } }
                ]
            });
            var encoder = device.createCommandEncoder();
            var pass = encoder.beginComputePass();
            pass.setPipeline(pipeline);
            pass.setBindGroup(0, bindGroup);
            pass.dispatchWorkgroups(1, 1, 1);
            pass.end();
            encoder.copyBufferToBuffer(outBuf, 0, readBuf, 0, 256);
            device.queue.submit([encoder.finish()]);
            await readBuf.mapAsync(GPUMapMode.READ);
            var data = new Float32Array(readBuf.getMappedRange().slice(0));
            readBuf.unmap();
            if (data[0] !== 3.0) return fail('wrong_value', String(data[0]));
            return true;
        } catch (err) {
            return fail(step + '_threw', err);
        } finally {
            try { device && device.destroy && device.destroy(); } catch (_) {}
        }
    };

    // Test hooks — fp16 helpers + dtype detector, from the shared module.
    AIUpscaleCore._test = {
        f32ToF16: S.f32ToF16,
        f16ToF32: S.f16ToF32,
        float32ArrayToFloat16Uint16: S.float32ArrayToFloat16Uint16,
        float16Uint16ToFloat32Array: S.float16Uint16ToFloat32Array,
        detectModelInputDtype: S.detectModelInputDtype
    };
    global.AIUpscaleCore = AIUpscaleCore;
})(typeof window !== 'undefined' ? window : this);
